import { __awaiter, __generator } from '../../_virtual/_tslib.js';
import { defineComponent, toRef, ref, computed, watchEffect, watch, onMounted, onBeforeUnmount, nextTick } from '@vue/composition-api';
import __vue_component__ from '../SdsButtonIcon/SdsButtonIcon.vue.js';
import __vue_component__$3 from '../SdsContentLoader/SdsContentLoader.vue.js';
import __vue_component__$1 from '../SdsIcon/SdsIcon.vue.js';
import { respondToBelow, ScreenWidth } from '@superbet-group/web.lib.vue-utilities';
import __vue_component__$2 from '../SdsDrawerSelect/SdsDrawerSelect.vue.js';
import __vue_component__$4 from '../SdsInputSearch/SdsInputSearch.vue.js';
import __vue_component__$5 from '../SdsOverlaySelect/SdsOverlaySelect.vue.js';
import { useOptionsSearch } from './useOptionsSearch.js';
import { isPointInsideRect } from '../utils/isPointInsideRect.js';

var script = defineComponent({
    name: 'SdsInputDropdown',
    props: {
        size: {
            type: String,
            required: false,
            default: 'lg',
        },
        label: {
            type: String,
            required: true,
        },
        selectedOption: {
            type: String,
            required: false,
            default: undefined,
        },
        options: {
            type: Array,
            required: true,
        },
        areOptionsLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
        name: {
            type: String,
            required: true,
        },
        placeholder: {
            type: String,
            required: false,
            default: undefined,
        },
        isDisabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        errorMessage: {
            type: String,
            required: false,
            default: '',
        },
        autocomplete: {
            type: String,
            required: false,
            default: 'off',
        },
        isCheckmarkEnabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        isRequired: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasClearIcon: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    /* eslint-disable sonarjs/no-duplicate-string */
    emits: [
        'update:selectedOption',
        'update:search',
        'focus',
        'blur',
        'expand',
        'collapse',
    ],
    setup: function (props, _a) {
        var emit = _a.emit;
        var options = toRef(props, 'options');
        var rootElement = ref(null);
        var searchInput = ref(null);
        var buttonInput = ref(null);
        var dropdown = ref(null);
        var isItemDisabled = ref(false);
        var _b = useOptionsSearch(options), query = _b.query, hasQuery = _b.hasQuery, hasNoResults = _b.hasNoResults, displayedOptions = _b.displayedOptions, clearQuery = _b.clearQuery, updateQuery = _b.updateQuery;
        var isTouched = ref(false);
        var isFocused = ref(false);
        var positioning = ref('bottom');
        var isInvalid = computed(function () { return props.isRequired && (!!props.errorMessage || !props.selectedOption && isTouched.value && !isFocused.value); });
        var isExpanded = ref(false);
        var hasAssignedValue = computed(function () { return props.options.some(function (option) { return option.value === props.selectedOption; }); });
        var selectedOptionLabel = computed(function () {
            var _a;
            return (_a = props.options.find(function (option) { return option.value === props.selectedOption; })) === null || _a === void 0 ? void 0 : _a.name;
        });
        var displayValue = computed(function () {
            var _a;
            if (!isFocused.value && hasQuery.value) {
                return query.value;
            }
            else {
                return (_a = selectedOptionLabel.value) !== null && _a !== void 0 ? _a : '';
            }
        });
        var stateIcon = computed(function () {
            if (!isFocused.value && isInvalid.value) {
                return 'status-danger-filled';
            }
            else if (!isFocused.value && props.isCheckmarkEnabled && hasAssignedValue.value) {
                return 'status-checkmark';
            }
            else if (isFocused.value && (hasQuery.value || hasAssignedValue.value)) {
                return 'navigation-close-alt-filled';
            }
            else {
                return '';
            }
        });
        var inputAttrs = computed(function () { return ({
            ref: searchInput,
            id: props.name,
            placeholder: isFocused.value && selectedOptionLabel.value ? selectedOptionLabel.value : undefined,
            value: isFocused.value ? query.value : displayValue.value,
            disabled: props.isDisabled,
            autocomplete: props.autocomplete,
        }); });
        var inputOn = computed(function () { return ({
            click: toggleOptions,
            'keydown.enter': toggleOptions,
            focus: emitFocus,
            blur: emitBlur,
        }); });
        function onSearchInputChange(value) {
            updateQuery(value !== null && value !== void 0 ? value : '');
            if (hasQuery.value) {
                isExpanded.value = true;
            }
        }
        function clearValue(event) {
            var _a;
            event.stopImmediatePropagation();
            clearQuery();
            if (props.selectedOption) {
                emit('update:selectedOption', null);
                (_a = searchInput.value) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        function toggleOptions(event) {
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (props.isDisabled) {
                                return [2 /*return*/];
                            }
                            event.stopPropagation();
                            isExpanded.value = !isExpanded.value;
                            return [4 /*yield*/, nextTick()];
                        case 1:
                            _a.sent();
                            return [2 /*return*/];
                    }
                });
            });
        }
        function closeDropdownOnClickOutside(mouseEvent) {
            var _a;
            var domRects = [
                (_a = dropdown.value) === null || _a === void 0 ? void 0 : _a.$el,
                searchInput.value,
                buttonInput.value,
                document.querySelector('.e2e-dropdown-input-modal'),
            ].filter(function (it) { return it != null; }).map(function (it) { return it.getBoundingClientRect(); });
            var point = new DOMPoint(mouseEvent.clientX, mouseEvent.clientY);
            if (domRects.every(function (domRect) { return domRect && !isPointInsideRect(point, domRect); })) {
                isExpanded.value = false;
                clearQuery();
            }
        }
        function passFocusToSearchInput() {
            var _a;
            (_a = searchInput.value) === null || _a === void 0 ? void 0 : _a.focus();
            isExpanded.value = !isExpanded.value;
        }
        function selectOption(option) {
            if (option.value === props.selectedOption) {
                emit('update:selectedOption', null);
                clearQuery();
            }
            else {
                emit('update:selectedOption', option.value);
                clearQuery();
                isExpanded.value = false;
            }
        }
        function closeWithEsc(event) {
            if (isExpanded.value) {
                toggleOptions(event);
            }
        }
        function emitFocus(event) {
            isFocused.value = true;
            isTouched.value = true;
            emit('focus', event);
        }
        function emitBlur(event) {
            var _a;
            // @ts-ignore
            if (((_a = event.relatedTarget) === null || _a === void 0 ? void 0 : _a.id) === "".concat(props.name, "-clear-btn")) {
                return;
            }
            isFocused.value = false;
            emit('blur', event);
        }
        watchEffect(function () {
            if (isExpanded.value) {
                emit('expand');
            }
            else {
                emit('collapse');
            }
        });
        watch(isExpanded, function (expanded) {
            if (!expanded || !rootElement.value) {
                return;
            }
            var distanceFromTopWithScroll = rootElement.value.getBoundingClientRect().top;
            var distanceFromBottomWithScroll = window.innerHeight - rootElement.value.getBoundingClientRect().bottom;
            if (distanceFromTopWithScroll > distanceFromBottomWithScroll) {
                positioning.value = 'top';
            }
            else {
                positioning.value = 'bottom';
            }
        });
        watch(query, function (searchQuery) {
            emit('update:search', searchQuery);
        }, { immediate: true });
        onMounted(function () {
            window.addEventListener('mousedown', closeDropdownOnClickOutside);
        });
        onBeforeUnmount(function () {
            window.removeEventListener('mousedown', closeDropdownOnClickOutside);
        });
        return {
            dropdown: dropdown,
            rootElement: rootElement,
            searchInput: searchInput,
            buttonInput: buttonInput,
            displayValue: displayValue,
            query: query,
            hasNoResults: hasNoResults,
            displayedOptions: displayedOptions,
            selectedOptionLabel: selectedOptionLabel,
            stateIcon: stateIcon,
            isInvalid: isInvalid,
            isTouched: isTouched,
            isFocused: isFocused,
            isExpanded: isExpanded,
            isErrorIcon: computed(function () { return !isFocused.value && isInvalid.value; }),
            isClearIcon: computed(function () { return ((isFocused.value && hasQuery.value) || !!selectedOptionLabel.value) && props.hasClearIcon; }),
            isCheckmarkIcon: computed(function () { return !isFocused.value && !isInvalid.value; }),
            isMobileLayout: computed(function () { return respondToBelow(ScreenWidth.tLg); }),
            passFocusToSearchInput: passFocusToSearchInput,
            onSearchInputChange: onSearchInputChange,
            clearValue: clearValue,
            emitFocus: emitFocus,
            emitBlur: emitBlur,
            toggleOptions: toggleOptions,
            selectOption: selectOption,
            closeWithEsc: closeWithEsc,
            isItemDisabled: isItemDisabled,
            positioning: positioning,
            inputAttrs: inputAttrs,
            inputOn: inputOn,
        };
    },
    components: {
        SdsButtonIcon: __vue_component__,
        SdsIcon: __vue_component__$1,
        SdsDrawerSelect: __vue_component__$2,
        SdsContentLoader: __vue_component__$3,
        SdsInputSearch: __vue_component__$4,
        SdsOverlaySelect: __vue_component__$5,
    },
});

export default script;
